import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Trash2, Plus } from "lucide-react";

const TEXT_FIELDS = [
    ["hero_kicker", "Hero kicker"],
    ["hero_title", "Hero title"],
    ["hero_subtitle", "Hero subtitle"],
    ["who_we_are_title", "Who we are — title"],
    ["what_we_do_title", "What we do — title"],
    ["what_we_do_subtitle", "What we do — subtitle"],
    ["featured_programme_slug", "Featured programme slug"],
    ["cta_donate", "CTA: Donate"],
    ["cta_volunteer", "CTA: Volunteer"],
    ["cta_partner", "CTA: Partner"],
    ["about_vision", "About: Vision"],
    ["about_mission", "About: Mission"],
    ["csr_intro", "CSR intro"],
    ["donate_appeal", "Donate appeal"],
    ["donate_instructions", "Donate instructions"],
];

const LONG_FIELDS = new Set(["hero_subtitle", "who_we_are_body", "about_vision", "about_mission", "csr_intro", "donate_appeal", "donate_instructions"]);

export default function AdminContent() {
    const [form, setForm] = useState(null);
    useEffect(() => { api.get("/site-content").then((r) => setForm(r.data || {})); }, []);

    const save = async () => {
        try {
            await api.put("/site-content", form);
            toast.success("Content saved");
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    if (!form) return <div className="text-slate-500">Loading…</div>;

    const updateArr = (key, i, val) => {
        const next = [...(form[key] || [])];
        next[i] = val;
        setForm({ ...form, [key]: next });
    };
    const addArr = (key, empty) => setForm({ ...form, [key]: [...(form[key] || []), empty] });
    const rmArr = (key, i) => setForm({ ...form, [key]: (form[key] || []).filter((_, idx) => idx !== i) });

    return (
        <div data-testid="admin-content" className="max-w-4xl">
            <h1 className="text-2xl font-semibold mb-1">Site content</h1>
            <p className="text-sm text-slate-500 mb-6">Homepage, About page, CSR and Donate copy.</p>

            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
                <h2 className="font-semibold">Text blocks</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block sm:col-span-2">
                        <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Who we are — body</div>
                        <textarea rows="4" value={form.who_we_are_body || ""} onChange={(e) => setForm({ ...form, who_we_are_body: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm resize-none" />
                    </label>
                    {TEXT_FIELDS.map(([k, l]) => (
                        <label key={k} className={`block ${LONG_FIELDS.has(k) ? "sm:col-span-2" : ""}`}>
                            <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">{l}</div>
                            {LONG_FIELDS.has(k) ? (
                                <textarea rows="3" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm resize-none" />
                            ) : (
                                <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                            )}
                        </label>
                    ))}
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 mt-6">
                <div className="flex items-center justify-between">
                    <h2 className="font-semibold">About — Core Values</h2>
                    <button onClick={() => addArr("about_values", { title: "", text: "" })} className="text-sm text-brand-forest font-semibold inline-flex items-center gap-1"><Plus size={14} /> Add value</button>
                </div>
                {(form.about_values || []).map((v, i) => (
                    <div key={`value-${i}-${v.title || ""}`} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
                        <input placeholder="Title" value={v.title} onChange={(e) => updateArr("about_values", i, { ...v, title: e.target.value })} className="sm:col-span-4 px-3 py-2 rounded border border-slate-200 text-sm" />
                        <textarea rows="2" placeholder="Description" value={v.text} onChange={(e) => updateArr("about_values", i, { ...v, text: e.target.value })} className="sm:col-span-7 px-3 py-2 rounded border border-slate-200 text-sm resize-none" />
                        <button onClick={() => rmArr("about_values", i)} className="sm:col-span-1 p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
                    </div>
                ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 mt-6">
                <div className="flex items-center justify-between">
                    <h2 className="font-semibold">CSR — Focus areas</h2>
                    <button onClick={() => addArr("csr_focus_areas", "")} className="text-sm text-brand-forest font-semibold inline-flex items-center gap-1"><Plus size={14} /> Add</button>
                </div>
                {(form.csr_focus_areas || []).map((a, i) => (
                    <div key={`csr-focus-${i}-${a}`} className="flex gap-2">
                        <input value={a} onChange={(e) => updateArr("csr_focus_areas", i, e.target.value)} className="flex-1 px-3 py-2 rounded border border-slate-200 text-sm" />
                        <button onClick={() => rmArr("csr_focus_areas", i)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
                    </div>
                ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 mt-6">
                <div className="flex items-center justify-between">
                    <h2 className="font-semibold">CSR — Why partner with us</h2>
                    <button onClick={() => addArr("csr_why_partner", "")} className="text-sm text-brand-forest font-semibold inline-flex items-center gap-1"><Plus size={14} /> Add</button>
                </div>
                {(form.csr_why_partner || []).map((a, i) => (
                    <div key={`csr-why-${i}-${a}`} className="flex gap-2">
                        <input value={a} onChange={(e) => updateArr("csr_why_partner", i, e.target.value)} className="flex-1 px-3 py-2 rounded border border-slate-200 text-sm" />
                        <button onClick={() => rmArr("csr_why_partner", i)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
                    </div>
                ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 mt-6">
                <h2 className="font-semibold">Donate — Suggested amounts (₹)</h2>
                <input
                    value={(form.donate_amounts || []).join(", ")}
                    onChange={(e) => setForm({ ...form, donate_amounts: e.target.value.split(",").map((s) => parseInt(s.trim(), 10)).filter(Boolean) })}
                    className="w-full px-3 py-2 rounded border border-slate-200 text-sm"
                    placeholder="e.g. 500, 1000, 2500, 5000, 10000"
                />
            </div>

            <div className="mt-6 flex justify-end">
                <button onClick={save} data-testid="content-save" className="px-5 py-2.5 rounded-md bg-brand-forest text-white text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save content</button>
            </div>
        </div>
    );
}
